import { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import { postFrameAgreementApplicationAPI } from '@/api/customerApi'
import Button from '@/components/form/Button'
import Input from '@/components/form/Input'
import ReactSelect from '@/components/form/ReactSelect'
import { Translation } from '@/components/i18n/Translation'
import LoaderCircle from '@/components/util/LoaderCircle'
import {
  getCustomerId,
  getInsuranceTypes,
  getVendorRates,
  getVendorRatesForType,
  getVendorRateTypes,
} from '@/ducks/customerReducer'
import useApiCaller from '@/hooks/useApiCaller'
import { getInsuranceTypeTextIdFromCode, getLeaseProductTypeTextIdFromCode } from '@/util/customers'

import './credit-frame-application.scss'

const CreditFrameApplication = ({ reload = () => {}, close = () => {} }) => {
  const vendorRateTypes = useSelector(getVendorRateTypes)
  const insuranceTypes = useSelector(getInsuranceTypes)
  const vendorRates = useSelector(getVendorRates)
  const customerId = useSelector(getCustomerId)

  const [form, setForm] = useState<any>({})
  const [rates, setRates] = useState({})

  // @ts-ignore
  const setFormProp = (prop, value) => setForm({ ...form, [prop]: value })
  // @ts-ignore
  const setRate = (rateType, value) =>
    setRates({
      ...rates,
      [rateType]: value,
    })

  useEffect(() => {
    // set default rates
    const rates = vendorRateTypes.reduce(
      (acc, cur) => ({
        // @ts-ignore
        ...acc,
        // @ts-ignore
        [cur]: vendorRates.find(rate => rate.product_type === cur).month_duration,
      }),
      {}
    )
    // @ts-ignore
    setRates(rates)
  }, [vendorRateTypes, vendorRates])

  const { callApi, isLoading: isPosting } = useApiCaller({
    // @ts-ignore
    action: () => postFrameAgreementApplicationAPI({ customerId, form, rates }),
    showProgressPopup: false,
  })

  const submit = async () => {
    await callApi()
    reload()
    close()
  }
  // @ts-ignore
  const insuranceTypeOptions = insuranceTypes.map(type => ({
    value: type.id,
    label: <Translation id={getInsuranceTypeTextIdFromCode(type.code)} />,
  }))

  const isFormValid = () => {
    const {
      signatory,
      phone,
      mail,
      vendor_name,
      vendor_org_number,
      vendor_contact,
      application_amount,
      insurance_id,
    } = form
    if (!signatory) {
      return false
    }
    if (!phone) {
      return false
    }
    if (!mail) {
      return false
    }
    if (!vendor_name) {
      return false
    }
    if (!vendor_org_number) {
      return false
    }
    if (!vendor_contact) {
      return false
    }
    if (!application_amount || application_amount <= 0) {
      return false
    }
    if (!insurance_id) {
      return false
    }
    return true
  }

  return isPosting ? (
    <LoaderCircle />
  ) : (
    <div className="credit-frame-application">
      <h2>
        <Translation id="Apply for credit" />
      </h2>

      <div className="credit-frame-application__form">
        <h3>
          <Translation id="Application details" />
        </h3>

        <Input
          label={<Translation id="Authorized Signatory" />}
          onChange={e => setFormProp('signatory', e.target.value)}
        />

        <Input
          label={<Translation id="Contact Phone" />}
          onChange={e => setFormProp('phone', e.target.value)}
        />

        <Input
          label={<Translation id="Contact Email" />}
          onChange={e => setFormProp('mail', e.target.value)}
          type="email"
        />

        <Input
          label={<Translation id="Supplier Name" />}
          onChange={e => setFormProp('vendor_name', e.target.value)}
        />

        <Input
          label={<Translation id="Supplier Orgnbr" />}
          onChange={e => setFormProp('vendor_org_number', e.target.value)}
        />

        <Input
          label={<Translation id="Supplier Contact Person" />}
          onChange={e => setFormProp('vendor_contact', e.target.value)}
        />

        <Input
          label={<Translation id="Frame Size" />}
          onChange={e => setFormProp('application_amount', e.target.value)}
          type="number"
        />

        <div className="input-container">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label>
            <Translation id="Insurance" />
          </label>

          <ReactSelect
            options={insuranceTypeOptions}
            // @ts-ignore
            onChange={value => setFormProp('insurance_id', value)}
          />
        </div>

        <Input
          label={<Translation id="Comment" />}
          onChange={e => setFormProp('comment', e.target.value)}
        />
      </div>

      <div className="credit-frame-application__rates">
        <h3>
          <Translation id="Lease durations" />
        </h3>

        {vendorRateTypes.map(type => (
          // @ts-ignore
          <VendorRateType rate={rates[type]} type={type} setRate={setRate} key={type} />
        ))}
      </div>

      <div className="credit-frame-application__options">
        <Button onClick={submit} disabled={!isFormValid()}>
          <Translation id="Submit application" />
        </Button>

        <Button onClick={close}>
          <Translation id="Cancel" />
        </Button>
      </div>
    </div>
  )
}

const VendorRateType = ({ type, rate, setRate }: any) => {
  const rates = useSelector(state => getVendorRatesForType(state, type))
  // @ts-ignore
  const options = rates.map(rate => ({
    value: rate.month_duration,
    label: (
      <Translation
        id="{duration} months ({rate}%)"
        duration={rate.month_duration}
        rate={rate.rate}
      />
    ),
  }))

  return (
    <div className="input-container">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label>
        <Translation id={getLeaseProductTypeTextIdFromCode(type)} />
      </label>

      <ReactSelect
        options={options}
        // @ts-ignore
        onChange={value => setRate(type, value)}
        value={rate}
        label={type}
      />
    </div>
  )
}

export default CreditFrameApplication
